// §5.4 — Windows 11 window semantics.
//
// This is the policy the snap engine was built against: six layouts and Snap Assist on.
//
// **On "six" (D-105, resolved by John 2026-08-03).** §5.4's cell reads "**4-zone and 6-zone
// layouts** (L/R halves; L/R/full-height thirds; 2×2 quadrants; 1-large-2-small)" — four names in
// the parenthetical and a bold phrase that reads as a zone *count*. Both readings are honoured
// rather than one traded for the other:
//
//   • `full` carries the **top edge**. `onEdgeDrag` is the only hook §5.4 gives a skin for it,
//     dragging to the top of the screen maximizes on Windows, and a zone is only reachable through
//     a layout `snapZones` returns. House's cell is written the same way — "halves + full".
//   • `grid3x2` is the literal **6-zone** layout, and the widest thing the picker offers.
//
// Zone ids are unique across layouts on purpose: `layoutOf` finds a zone's layout by id, and two
// layouts both calling a zone `left` would put a window in the wrong Snap Group (§5.4).
import type { Edge, SnapLayout, SnapZone, WindowManagerPolicy } from '@hangar/shared';

// §5.4's fraction→pixel conversion lives in `kernel/wm/geometry` because the snap engine needs
// it and may not import a skin (§6.5). Re-exported so this skin's own callers read naturally.
export { zoneRect } from '@/kernel/wm/geometry';

const THIRD = 1 / 3;
const TWO_THIRDS = 2 / 3;

/** §5.4's six. Zone rects are fractions of the work area, 0..1. */
export const WINDOWS_LAYOUTS: readonly SnapLayout[] = [
  {
    id: 'halves',
    label: 'Halves',
    zones: [
      { id: 'halves-left', rect: { x: 0, y: 0, w: 0.5, h: 1 } },
      { id: 'halves-right', rect: { x: 0.5, y: 0, w: 0.5, h: 1 } },
    ],
  },
  {
    id: 'thirds',
    label: 'Thirds',
    zones: [
      { id: 'thirds-left', rect: { x: 0, y: 0, w: THIRD, h: 1 } },
      { id: 'thirds-centre', rect: { x: THIRD, y: 0, w: THIRD, h: 1 } },
      { id: 'thirds-right', rect: { x: TWO_THIRDS, y: 0, w: THIRD, h: 1 } },
    ],
  },
  {
    id: 'quadrants',
    label: 'Quadrants',
    zones: [
      { id: 'quad-top-left', rect: { x: 0, y: 0, w: 0.5, h: 0.5 } },
      { id: 'quad-top-right', rect: { x: 0.5, y: 0, w: 0.5, h: 0.5 } },
      { id: 'quad-bottom-left', rect: { x: 0, y: 0.5, w: 0.5, h: 0.5 } },
      { id: 'quad-bottom-right', rect: { x: 0.5, y: 0.5, w: 0.5, h: 0.5 } },
    ],
  },
  {
    // §5.4's "1-large-2-small": one two-thirds window with two stacked beside it.
    id: 'focus',
    label: 'One large, two small',
    zones: [
      { id: 'focus-main', rect: { x: 0, y: 0, w: TWO_THIRDS, h: 1 } },
      { id: 'focus-top', rect: { x: TWO_THIRDS, y: 0, w: THIRD, h: 0.5 } },
      { id: 'focus-bottom', rect: { x: TWO_THIRDS, y: 0.5, w: THIRD, h: 0.5 } },
    ],
  },
  {
    id: 'full',
    label: 'Full',
    zones: [{ id: 'full', rect: { x: 0, y: 0, w: 1, h: 1 } }],
  },
  {
    // §5.4's "6-zone": three across, two down. Windows offers this on a wide display.
    id: 'grid3x2',
    label: 'Six',
    zones: [
      { id: 'grid-top-left', rect: { x: 0, y: 0, w: THIRD, h: 0.5 } },
      { id: 'grid-top-centre', rect: { x: THIRD, y: 0, w: THIRD, h: 0.5 } },
      { id: 'grid-top-right', rect: { x: TWO_THIRDS, y: 0, w: THIRD, h: 0.5 } },
      { id: 'grid-bottom-left', rect: { x: 0, y: 0.5, w: THIRD, h: 0.5 } },
      { id: 'grid-bottom-centre', rect: { x: THIRD, y: 0.5, w: THIRD, h: 0.5 } },
      { id: 'grid-bottom-right', rect: { x: TWO_THIRDS, y: 0.5, w: THIRD, h: 0.5 } },
    ],
  },
];

function zone(id: string): SnapZone | null {
  for (const layout of WINDOWS_LAYOUTS) {
    const found = layout.zones.find((z) => z.id === id);
    if (found) return found;
  }
  return null;
}

/** §5.4 — what each display edge means on this skin. Corners are quadrants, the top maximizes. */
const EDGE_ZONES: Record<Edge, string | null> = {
  left: 'halves-left',
  right: 'halves-right',
  top: 'full',
  bottom: null,
  topLeft: 'quad-top-left',
  topRight: 'quad-top-right',
  bottomLeft: 'quad-bottom-left',
  bottomRight: 'quad-bottom-right',
};

export const windowsPolicy: WindowManagerPolicy = {
  onMaximizeButton: () => 'maximize',

  onEdgeDrag(edge) {
    const id = EDGE_ZONES[edge];
    return id === null ? null : zone(id);
  },

  snapZones: () => WINDOWS_LAYOUTS.map((layout) => ({ ...layout, zones: [...layout.zones] })),

  switcherModel: () => 'windows',
  minimizeTarget: () => 'barSlot',
  chromeSide: () => 'right',
  // §5.4 — the one skin that has it. M7.6 is what makes it mean something.
  snapAssist: () => true,
};
